import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import { useUsersInOrg } from 'src/api/rpc/hooks/core/useUsersInOrg';
import { useDetachTaskFromAgent } from 'src/api/rpc/hooks/tasks/useTaskDetail';
import {
  SingleSelect,
  type SingleSelectOption,
} from 'src/components/common/SingleSelect';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { toast } from 'src/components/ui/toast';

export interface DetachTaskSnapshot {
  id: string;
  status: string;
  assigneeType: 'user' | 'agent' | null;
  assigneeUserId: string | null;
}

/**
 * Confirm taking a task away from its agent, optionally handing it to a
 * person. With no one picked, an agent assignee is cleared and a person who
 * already holds the task keeps it.
 */
export function DetachTaskFromAgentDialog({
  task,
  agentName,
  open,
  onOpenChange,
  onDetached,
}: {
  task: DetachTaskSnapshot;
  agentName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDetached?: () => void;
}) {
  const gt = useGT();
  const detach = useDetachTaskFromAgent(task.id);
  const { data: users } = useUsersInOrg();
  const [selected, setSelected] = useState<SingleSelectOption | null>(null);
  const options = (users ?? []).map((user) => ({
    id: user.id,
    label: user.name,
  }));
  const keepsCurrentUser =
    task.assigneeType === 'user' && task.assigneeUserId !== null;

  function close() {
    if (detach.isPending) return;
    setSelected(null);
    onOpenChange(false);
  }

  async function confirm() {
    try {
      await detach.mutateAsync({
        id: task.id,
        ...(selected ? { assigneeUserId: selected.id } : {}),
      });
      setSelected(null);
      onOpenChange(false);
      onDetached?.();
      toast.success(
        gt('Removed from {agentName}', {
          context: 'toast after a task is detached from its agent',
          agentName,
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not remove the task from the agent', {
              context: 'toast when detaching a task from its agent failed',
            }),
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: confirm removing a task from the agent that owns it">
              Detach from <Var>{agentName}</Var>
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog body: what detaching a task from its agent does">
              The task leaves this agent&apos;s queue and the agent will not
              pick it up again. A running or waiting task goes back to Pending.
            </T>
          </DialogDescription>
        </DialogHeader>
        {task.status === 'in_progress' && (
          <div
            className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="detach-running-warning"
          >
            <WarningIcon className="mt-0.5 size-4 shrink-0" />
            <T context="warning in the detach dialog when the agent is working the task right now">
              The agent is working on this task right now. It will be stopped
              from saving a result, but may finish its current step first.
            </T>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="detach-task-assignee">
            <T context="form label: optional person to hand the detached task to">
              Assign to (optional)
            </T>
          </Label>
          <SingleSelect
            allowClear
            id="detach-task-assignee"
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder={
              keepsCurrentUser
                ? gt('Keep the current assignee', {
                    context:
                      'placeholder on the detach picker when a person already holds the task',
                  })
                : gt('Leave unassigned', {
                    context:
                      'placeholder on the detach picker when no person holds the task',
                  })
            }
            searchPlaceholder={gt('Search people…', {
              context: 'search box in the detach-from-agent person picker',
            })}
            fullWidth
            disabled={detach.isPending}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={detach.isPending}>
            <T context="button, close the detach-from-agent dialog without changing the task">
              Cancel
            </T>
          </Button>
          <Button
            data-testid="confirm-detach-from-agent"
            disabled={detach.isPending}
            onClick={() => {
              void confirm();
            }}
          >
            {detach.isPending ? (
              <T context="button label while a task is being detached from its agent">
                Detaching…
              </T>
            ) : (
              <T context="button, confirm detaching the task from its agent">
                Detach from Agent
              </T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
